/**
 * Custom auth hook
 * Wraps Amplify's useAuthenticator with additional utilities
 */

import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useCallback, useState, useEffect } from 'react';

/**
 * Custom hook for authentication state and utilities
 * @returns {Object} Auth state and functions
 */
export function useAuth() {
  const { user, signOut, authStatus } = useAuthenticator((context) => [
    context.user,
    context.signOut,
    context.authStatus,
  ]);

  const [tenantId, setTenantId] = useState(null);
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  const isAuthenticated = authStatus === 'authenticated';
  const isLoading = authStatus === 'configuring' || (isAuthenticated && roleLoading);
  const isSuperAdmin = role === 'super-admin';
  const isAdmin = role === 'admin' || role === 'super-admin';

  /**
   * Get the current JWT token
   * @returns {Promise<string|null>}
   */
  const getToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get token:', error);
      return null;
    }
  }, []);

  /**
   * Get the decoded token payload
   * @returns {Promise<Object|null>}
   */
  const getTokenPayload = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.payload || null;
    } catch (error) {
      console.error('Failed to get token payload:', error);
      return null;
    }
  }, []);

  // Extract tenantId and role from token when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      setRoleLoading(true);
      getTokenPayload().then((payload) => {
        // tenantId stored in custom:tenant_id or custom:tenantId claim, or sub as fallback
        const tenant = payload?.['custom:tenant_id'] || payload?.['custom:tenantId'] || payload?.sub || null;
        setTenantId(tenant);
        // role stored in custom:role claim
        const userRole = payload?.['custom:role'] || 'viewer';
        setRole(userRole);
        setRoleLoading(false);
      });
    } else {
      setTenantId(null);
      setRole(null);
      setRoleLoading(false);
    }
  }, [isAuthenticated, getTokenPayload]);

  return {
    user,
    isAuthenticated,
    isLoading,
    tenantId,
    role,
    isAdmin,
    isSuperAdmin,
    signOut,
    getToken,
    getTokenPayload,
  };
}

export default useAuth;
